// Detects genuine in-page user interaction so the portals can keep an active
// session alive without prompting (AB#26125). Previously the Vue portals only
// treated successful API responses as "activity", so a user working within an
// already-loaded view (typing, clicking, scrolling) generated no signal and was
// ambushed by the inactivity popup near token expiry.
//
// The event set mirrors @syrenis/session (SessionProvider.tsx) so the Vue
// portals detect activity the same way as the newer Next.js portals. Raw
// mousemove is deliberately excluded (a resting cursor is not intent).
export const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart']

const DEFAULT_THROTTLE_MS = 2000

/**
 * Install throttled listeners for real user interaction and invoke `onActivity`
 * at most once per `throttleMs`. Returns a teardown function that removes them.
 *
 * `target` and `now` are injectable for testing; the defaults (window /
 * Date.now) are correct in the browser. `target` defaults to `window` to match
 * @syrenis/session and avoid relying on globalThis in older browsers.
 *
 * @param {() => void} onActivity - called on throttled user interaction
 * @param {{ throttleMs?: number, target?: EventTarget, now?: () => number }} [options]
 * @returns {() => void} teardown that removes the listeners
 */
export function installActivityListeners (onActivity, { throttleMs = DEFAULT_THROTTLE_MS, target = window, now = Date.now } = {}) {
	let last = Number.NEGATIVE_INFINITY
	const handler = () => {
		const timestamp = now()
		if (timestamp - last < throttleMs) return
		last = timestamp
		onActivity()
	}
	for (const event of ACTIVITY_EVENTS) {
		target.addEventListener(event, handler, { passive: true })
	}
	return () => {
		for (const event of ACTIVITY_EVENTS) {
			// removeEventListener only matches on type + handler + capture; `passive`
			// is irrelevant here, so we omit it (same handler reference is reused).
			target.removeEventListener(event, handler)
		}
	}
}
